<template>
  <div class="relative flex w-max flex-col">
    <div class="flex shrink-0 items-center gap-1 p-3 pb-0">
      <button
        type="button"
        class="flex h-8 grow items-center gap-1 rounded-md px-2 text-sm font-medium outline-2 outline-blue-500 transition hover:bg-card focus-visible:outline"
        :aria-expanded="view === 'quickjump'"
        aria-haspopup="true"
        @click="toggleView"
      >
        {{ monthYearDisplay }}

        <ChevronDownIcon
          class="size-3 text-muted-foreground transition"
          :class="view === 'quickjump' && 'rotate-180'"
        />
      </button>

      <button
        type="button"
        aria-label="Previous month"
        :disabled="!canNavigatePrev"
        class="grid size-8 shrink-0 place-items-center rounded-md outline-2 outline-blue-500 transition hover:bg-card focus-visible:outline disabled:pointer-events-none disabled:opacity-50"
        @click="navigateMonth(-1)"
      >
        <ChevronDownIcon class="size-4 rotate-90" />
      </button>

      <button
        type="button"
        aria-label="Next month"
        :disabled="!canNavigateNext"
        class="grid size-8 shrink-0 place-items-center rounded-md outline-2 outline-blue-500 transition hover:bg-card focus-visible:outline disabled:pointer-events-none disabled:opacity-50"
        @click="navigateMonth(1)"
      >
        <ChevronDownIcon class="size-4 -rotate-90" />
      </button>
    </div>

    <DateCalendarGrid
      v-if="view === 'grid'"
      ref="grid"
      v-model="cursor"
      :selected="selected"
      :min="minDate"
      :max="maxDate"
      @select="handleSelect"
    />
    <DateCalendarQuickjump
      v-else
      ref="quickjump"
      :initial-cursor="cursor"
      :min="minDate"
      :max="maxDate"
      @jump="handleJump"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import {
  addMonths,
  endOfDay,
  endOfMonth,
  formatISODate,
  formatMonthYear,
  parseISODate,
  startOfMonth,
} from "@/lib/time";

import DateCalendarGrid from "./DateCalendarGrid.vue";
import DateCalendarQuickjump from "./DateCalendarQuickjump.vue";

const modelValue = defineModel<string>({ required: true });
const selected = computed(() => parseISODate(modelValue.value) ?? undefined);

const props = defineProps<{
  min?: string;
  max?: string;
  autofocus?: boolean;
}>();

const emit = defineEmits<{
  select: [date: string];
}>();

const grid = useTemplateRef("grid");
const quickjump = useTemplateRef("quickjump");

const view = ref<"grid" | "quickjump">("grid");

const minDate = computed(() => {
  const min = props.min;
  if (!min) {
    return null;
  }

  return parseISODate(min);
});

const maxDate = computed(() => {
  const max = props.max;
  if (!max) {
    return null;
  }

  const date = parseISODate(max);
  if (!date) {
    return null;
  }

  return endOfDay(date);
});

const cursor = ref(
  (() => {
    const today = new Date();
    const min = minDate.value;

    if (min !== null && today < min) {
      return min;
    }

    return today;
  })()
);

const firstOfMonth = computed(() => startOfMonth(cursor.value));
const monthYearDisplay = computed(() => formatMonthYear(firstOfMonth.value));

const canNavigatePrev = computed(() => {
  if (!minDate.value) {
    return true;
  }
  return endOfMonth(addMonths(firstOfMonth.value, -1)) >= minDate.value;
});

const canNavigateNext = computed(() => {
  if (!maxDate.value) {
    return true;
  }

  return addMonths(firstOfMonth.value, 1) <= maxDate.value;
});

const navigateMonth = (delta: number): void => {
  cursor.value = startOfMonth(addMonths(cursor.value, delta));
};

const toggleView = (): void => {
  view.value = view.value === "grid" ? "quickjump" : "grid";

  if (view.value === "quickjump") {
    nextTick(() => quickjump.value?.focusSelectedMonth());
  } else {
    nextTick(() => grid.value?.focusCursor());
  }
};

const handleSelect = (date: Date | null): void => {
  const next = date !== null ? formatISODate(date) : "";

  modelValue.value = next;
  emit("select", next);
};

const handleJump = (date: Date): void => {
  cursor.value = date;
  view.value = "grid";
  nextTick(() => grid.value?.focusCursor());
};

// Sync cursor to model value
watch(
  modelValue,
  (value) => {
    if (!value) {
      return;
    }

    const parsed = parseISODate(value);

    if (parsed) {
      cursor.value = parsed;
    }
  },
  { immediate: true }
);

const focusCursorDate = (): void => {
  grid.value?.focusCursor();
};

onMounted(() => {
  if (props.autofocus) {
    focusCursorDate();
  }
});

defineExpose({ focusCursorDate });
</script>
